import type { CSSProperties, MutableRefObject } from 'react';
import { useCanvasStore } from '@/components/canvas/store/canvas.store';
import { motion } from 'motion/react';
import type {
  Element,
  LineElement as LineElementType,
  GroupElement as GroupElementType,
  TextElement as TextElementType,
  TableElement as TableElementType,
} from '@/components/canvas/document/publicTypes';
import {
  TextEditOverlay,
  EraserToolOverlay,
  AlignmentGuideLayer,
} from '@/components/canvas/workspace/overlays';
import { SelectionBoxLayer, HandleLayer } from '@/components/canvas/selection/index';
import { UnionSelectionChrome } from '@/components/canvas/selection/UnionSelectionChrome';
import { SelectionChromeFrame } from '@/components/canvas/selection/SelectionChromeFrame';
import { selectionChromeWorldMetrics } from '@/components/canvas/selection/selectionChrome';
import { PageSelectionLayer } from '@/components/canvas/page/overlays/PageSelectionOverlay';
import { ShapeDrawPreviewLayer } from '@/components/canvas/workspace/overlays/ShapeDrawPreviewLayer';
import { TableDrawSessionLayer } from '@/components/canvas/elements/table/overlays/TableDrawSessionLayer';
import type { ResizeHandle } from '@/components/canvas/transform';
import { getReferenceTextFontSize } from '@/components/canvas/elements/text/model/textResize';
import { getElementAbsolutePos } from '@/components/canvas/workspace/model/getElementAbsolutePos';
import { getLineChromeRect, getLineEndOffset } from '@/components/canvas/elements/shape/line/lineElementPatch';
import { getTableChromeRect } from '@/components/canvas/elements/table/utils/tableLayout';
import type { WorkspaceInteractionApi } from '@/components/canvas/workspace/controllers/useWorkspaceInteraction';
import { blockToSelectionElement } from '@/components/canvas/blocks/blockSelectionAdapter';

function tableHasSubSelection(
  elementId: string,
  selectedTableCells: WorkspaceInteractionApi['selectedTableCells'],
  selectedTableRow: WorkspaceInteractionApi['selectedTableRow'],
  selectedTableColumn: WorkspaceInteractionApi['selectedTableColumn'],
): boolean {
  if (selectedTableCells?.elementId === elementId) return true;
  if (selectedTableRow?.elementId === elementId) return true;
  if (selectedTableColumn?.elementId === elementId) return true;
  return false;
}

export interface InteractionLayerProps {
  size: { width: number; height: number };
  zoom: number;
  pageRef: MutableRefObject<HTMLDivElement | null>;
  interaction: WorkspaceInteractionApi;
  /**
   * 嵌入 `data-page` 内（与元素同页坐标系，叠在元素之上）。
   * 为 false 时使用 pageOffset 挂在 stage 世界坐标（已弃用，保留类型兼容）。
   */
  embedded?: boolean;
  pageOffset?: { x: number; y: number };
}

export function InteractionLayer({
  embedded = true,
  pageOffset = { x: 0, y: 0 },
  size,
  zoom,
  pageRef,
  interaction,
}: InteractionLayerProps) {
  const {
    elements,
    rootElements,
    visibleElements,
    selectedElementIds,
    editingGroupId,
    editingTextId,
    editingTableCellId,
    selectedTableColumn,
    selectedTableRow,
    selectedTableCells,
    elementDrag,
    dragOffset,
    hoveredElementId,
    isTextDrawing,
    textDrawRect,
    isShapeDrawing,
    shapeDrawPreview,
    isTableDrawing,
    tableDrawRect,
    tableDrawPending,
    setTableDrawRows,
    setTableDrawCols,
    confirmTableDraw,
    cancelTableDraw,
    eraserToolActive,
    liveEditingGroupRect,
    updateElement,
    setEditingTextId,
    editingTextIdRef,
    pushHistory,
    lineHandleState,
    setLineHandleState,
    lineHandlePreview,
    setLineHandlePreview,
    resizeState,
    setResizeState,
    resizePreview,
    setResizePreview,
    resizePreviewRef,
    groupResizeState,
    setGroupResizeState,
    groupResizePreview,
    setGroupResizePreview,
    groupResizePreviewRef,
    radiusDragState,
    setRadiusDragState,
    radiusPreview,
    setRadiusPreview,
    radiusPreviewRef,
    rotateState,
    setRotateState,
    rotatePreview,
    setRotatePreview,
    rotatePreviewRef,
    isPageSelected,
    pageResizePreview,
    startPageResize,
    onPageFrameDragStart,
    pageSize,
    useWorldSelectionChrome,
    pageUnionRect,
    startUnionResize,
    selectedPageIds,
    alignmentGuides,
    blockDrag,
    hideSelectionChrome,
    pageBlocks = [],
    hoveredBlockId = null,
    selectedBlockIds = [],
    blockDragOffset = { dx: 0, dy: 0 },
    blockResizePreview = null,
    blockResizeState = null,
  } = interaction;

  const editingTextIdFromStore = useCanvasStore((s) => s.editingTextId);

  const layerStyle: CSSProperties = embedded
    ? { left: 0, top: 0, width: size.width, height: size.height, zIndex: 5 }
    : {
        left: pageOffset.x,
        top: pageOffset.y,
        width: size.width,
        height: size.height,
        zIndex: 100,
      };

  return (
    <div className="pointer-events-none absolute" style={layerStyle}>
                  {(elementDrag || blockDrag) && alignmentGuides.length > 0 && (
                    <AlignmentGuideLayer guides={alignmentGuides} zoom={zoom} />
                  )}

                  {/* 页面选中框（无元素选中时） */}
                  {isPageSelected &&
                    !hideSelectionChrome &&
                    selectedElementIds.length === 0 &&
                    selectedBlockIds.length === 0 &&
                    selectedPageIds.length === 0 && (
                    <PageSelectionLayer
                      preview={pageResizePreview}
                      baseSize={pageSize}
                      zoom={zoom}
                      onStartPageResize={startPageResize}
                      onPageFrameDragStart={onPageFrameDragStart}
                    />
                  )}

                  {/* 文本 / 图形拖拽绘制预览 */}
                  {isTextDrawing && textDrawRect && (
                    <motion.div
                      className="absolute border-2 border-primary pointer-events-none"
                      style={{
                        left: textDrawRect.x,
                        top: textDrawRect.y,
                        width: textDrawRect.width,
                        height: textDrawRect.height,
                        background: 'rgba(var(--accent) / 0.08)',
                      }}
                    />
                  )}
                  {isShapeDrawing && shapeDrawPreview && (
                    <ShapeDrawPreviewLayer preview={shapeDrawPreview} />
                  )}
                  {(isTableDrawing || tableDrawPending) && (
                    <TableDrawSessionLayer
                      zoom={zoom}
                      drawRect={isTableDrawing ? tableDrawRect : null}
                      pending={tableDrawPending}
                      onRowsChange={setTableDrawRows}
                      onColsChange={setTableDrawCols}
                      onConfirm={confirmTableDraw}
                      onCancel={cancelTableDraw}
                    />
                  )}

                  {/* 文本编辑浮层 */}
                  <TextEditOverlay
                    key={editingTextId ?? 'none'}
                    editingTextId={editingTextId}
                    zoom={zoom}
                    allElements={visibleElements.filter((e) => e.type === 'text') as TextElementType[]}
                    onCommit={(id, update) => updateElement(id, update as never)}
                    onSizeChange={(id, patch) => updateElement(id, patch as never)}
                    onDismiss={(id: string) => {
                      // 仅当 editingTextId 仍指向该元素时才清除，
                      // 避免切换至另一文本元素时被此 blur 误清
                      if (editingTextIdRef.current === id) setEditingTextId(null);
                    }}
                    positionOffset={(() => {
                      // 若当前编辑的文本是组内子元素，传入组的画布绝对偏移
                      // （对嵌套组，需递归求绝对坐标而非直接取 grp.x/y）
                      if (!editingGroupId || !editingTextId) return undefined;
                      const grp = elements.find((g) => g.id === editingGroupId) as GroupElementType | undefined;
                      if (grp?.data.children.includes(editingTextId)) {
                        return getElementAbsolutePos(editingGroupId, elements);
                      }
                      return undefined;
                    })()}
                  />

                  {/* Hover 选择框（悬停但未选中的元素，无手柄） */}
                  {(() => {
                    // 普通模式：rootElements hover（锁定元素不显示 hover 框）
                    const rootHover = rootElements.filter((el) =>
                      el.id === hoveredElementId &&
                      !el.interaction.locked &&
                      !selectedElementIds.includes(el.id) &&
                      el.type !== 'line' &&
                      el.id !== editingTextId &&
                      el.id !== editingGroupId &&
                      !elementDrag
                    );
                    // 组内编辑模式：子元素 hover（画布绝对坐标，支持嵌套组）
                    const childHover: typeof rootElements = [];
                    if (editingGroupId && hoveredElementId && !selectedElementIds.includes(hoveredElementId) && !elementDrag) {
                      const grp = elements.find((g) => g.id === editingGroupId) as GroupElementType | undefined;
                      if (grp?.data.children.includes(hoveredElementId)) {
                        const child = elements.find((e) => e.id === hoveredElementId);
                        if (child && !child.interaction.locked && child.type !== 'line' && child.id !== editingTextId) {
                          const grpAbsPos = getElementAbsolutePos(editingGroupId, elements);
                          childHover.push({
                            ...child,
                            layout: {
                              ...child.layout,
                              x: grpAbsPos.x + child.layout.x,
                              y: grpAbsPos.y + child.layout.y,
                            },
                          });
                        }
                      }
                    }
                    const hoverEls = [...rootHover, ...childHover];
                    if (!hoverEls.length) return null;
                    return (
                      <SelectionBoxLayer
                        elements={hoverEls}
                        dragOffset={{ dx: 0, dy: 0 }}
                        zoom={zoom}
                      />
                    );
                  })()}

                  {/* 功能块 hover / 选中（与元素相同 SelectionBoxLayer + 实线框） */}
                  {(() => {
                    const resizeId = blockResizeState?.id ?? null;
                    const blockHover = pageBlocks.filter(
                      (b) =>
                        b.id === hoveredBlockId &&
                        !selectedBlockIds.includes(b.id) &&
                        !elementDrag,
                    );
                    const blockSelected = pageBlocks.filter((b) => selectedBlockIds.includes(b.id));
                    const hoverEls = blockHover.map((b) =>
                      blockToSelectionElement(b, { dx: 0, dy: 0 }, null, null),
                    );
                    const selectedEls = blockSelected.map((b) =>
                      blockToSelectionElement(
                        b,
                        blockDragOffset,
                        blockResizePreview,
                        resizeId,
                      ),
                    );
                    return (
                      <>
                        {hoverEls.length > 0 ? (
                          <SelectionBoxLayer
                            elements={hoverEls}
                            dragOffset={{ dx: 0, dy: 0 }}
                            zoom={zoom}
                          />
                        ) : null}
                        {selectedEls.length > 0 && !hideSelectionChrome ? (
                          <SelectionBoxLayer
                            elements={selectedEls}
                            dragOffset={{ dx: 0, dy: 0 }}
                            zoom={zoom}
                          />
                        ) : null}
                      </>
                    );
                  })()}

                  {/* 选中选框（根元素）；工作区圈选时由 WorldSelectionOverlay 绘制 */}
                  {!useWorldSelectionChrome && !hideSelectionChrome && selectedBlockIds.length === 0 && (
                    <SelectionBoxLayer
                      elements={rootElements.filter((el) => {
                        if (!selectedElementIds.includes(el.id) || el.type === 'line' || el.id === editingGroupId)
                          return false;
                        if (
                          el.type === 'table' &&
                          tableHasSubSelection(
                            el.id,
                            selectedTableCells,
                            selectedTableRow,
                            selectedTableColumn,
                          )
                        ) {
                          return false;
                        }
                        return true;
                      })}
                      dragOffset={elementDrag ? dragOffset : { dx: 0, dy: 0 }}
                      zoom={zoom}
                    />
                  )}

                  {/* 页面内多选：联合包围盒 + 四角手柄（与逐条选框并存） */}
                  {!useWorldSelectionChrome &&
                    !hideSelectionChrome &&
                    pageUnionRect &&
                    selectedElementIds.length >= 2 && (
                      <UnionSelectionChrome
                        rect={pageUnionRect}
                        zoom={zoom}
                        dragOffset={elementDrag ? dragOffset : { dx: 0, dy: 0 }}
                        onStartUnionResize={startUnionResize}
                      />
                    )}

                  {/* 组内编辑：实时动态虚线组边框（随子元素移动而更新） */}
                  {liveEditingGroupRect && (() => {
                    const { inset, pad } = selectionChromeWorldMetrics(zoom);
                    return (
                      <SelectionChromeFrame
                        left={liveEditingGroupRect.x - inset}
                        top={liveEditingGroupRect.y - inset}
                        width={liveEditingGroupRect.width + pad}
                        height={liveEditingGroupRect.height + pad}
                        zoom={zoom}
                        dashed
                        opacity={0.55}
                      />
                    );
                  })()}

                  {/* 组内编辑：选中子元素的选框（子元素坐标 + 组画布绝对偏移 = 画布绝对坐标） */}
                  {editingGroupId && (() => {
                    const grp = elements.find((e) => e.id === editingGroupId) as GroupElementType | undefined;
                    if (!grp) return null;
                    // 对嵌套组，需用画布绝对坐标作为基准（而非直接用 grp.x/y）
                    const grpAbsPos = getElementAbsolutePos(editingGroupId, elements);
                    const childrenSelected = elements
                      .filter((e) => selectedElementIds.includes(e.id) && grp.data.children.includes(e.id) && e.type !== 'line')
                      .map((child) => ({
                        ...child,
                        layout: {
                          ...child.layout,
                          x: grpAbsPos.x + child.layout.x,
                          y: grpAbsPos.y + child.layout.y,
                        },
                      }));
                    if (!childrenSelected.length) return null;
                    return (
                      <SelectionBoxLayer
                        elements={childrenSelected}
                        dragOffset={elementDrag ? dragOffset : { dx: 0, dy: 0 }}
                        zoom={zoom}
                      />
                    );
                  })()}

                  {/* 橡皮擦覆盖层 */}
                  {eraserToolActive && (
                    <EraserToolOverlay
                      pageWidth={size.width}
                      pageHeight={size.height}
                      zoom={zoom}
                    />
                  )}

                  {/* 单选把手（文本编辑态时不显示） */}
                  {selectedElementIds.length === 1 && !hideSelectionChrome && (() => {
                    let el = rootElements.find((e) => selectedElementIds.includes(e.id));
                    let groupChildOffset = { x: 0, y: 0 };
                    // 若未找到，检查是否为组内子元素
                    if (!el && editingGroupId) {
                      const grp = elements.find((g) => g.id === editingGroupId) as GroupElementType | undefined;
                      if (grp) {
                        const childId = selectedElementIds[0];
                        if (grp.data.children.includes(childId)) {
                          el = elements.find((e) => e.id === childId) ?? undefined;
                          // 对嵌套组，需用画布绝对坐标（递归追溯父组链）
                          if (el) groupChildOffset = getElementAbsolutePos(editingGroupId, elements);
                        }
                      }
                    }
                    if (!el) return null;
                    if (editingTextId && el.id === editingTextId) return null;
                    if (
                      el.type === 'table' &&
                      tableHasSubSelection(
                        el.id,
                        selectedTableCells,
                        selectedTableRow,
                        selectedTableColumn,
                      )
                    ) {
                      return null;
                    }
                    const dx = elementDrag ? dragOffset.dx : 0;
                    const dy = elementDrag ? dragOffset.dy : 0;
                    const isLine = el.type === 'line';
                    const lineEl = isLine ? (el as LineElementType) : null;
                    const lineEnd = isLine && lineEl
                      ? lineHandleState?.id === el.id && lineHandlePreview
                        ? { endX: lineHandlePreview.endX, endY: lineHandlePreview.endY }
                        : getLineEndOffset(lineEl)
                      : { endX: 0, endY: 0 };
                    const lineEndX = lineEnd.endX;
                    const lineEndY = lineEnd.endY;
                    const lineRect = isLine && lineEl
                      ? lineHandleState?.id === el.id && lineHandlePreview
                        ? getLineChromeRect(
                            {
                              ...lineEl,
                              layout: { ...lineEl.layout, x: lineHandlePreview.x, y: lineHandlePreview.y },
                              data: { start: { x: 0, y: 0 }, end: { x: lineHandlePreview.endX, y: lineHandlePreview.endY } },
                              size: {
                                width: Math.max(1, Math.abs(lineHandlePreview.endX)),
                                height: Math.max(1, Math.abs(lineHandlePreview.endY)),
                              },
                            },
                            { dx, dy },
                          )
                        : getLineChromeRect(lineEl, { dx, dy })
                      : null;
                    const isGroup = el.type === 'group';
                    // groupChildOffset 为非零时，说明是组内子元素，需加上组偏移得到画布绝对坐标
                    const gox = groupChildOffset.x, goy = groupChildOffset.y;
                    const rect = (isGroup && groupResizeState?.id === el.id && groupResizePreview)
                      ? { ...groupResizePreview, x: groupResizePreview.x + gox, y: groupResizePreview.y + goy }
                      : resizeState?.id === el.id && resizePreview
                        ? { ...resizePreview, x: resizePreview.x + gox, y: resizePreview.y + goy }
                        : isLine && lineRect
                          ? { ...lineRect, x: lineRect.x + gox, y: lineRect.y + goy }
                          : el.type === 'table'
                            ? getTableChromeRect(el as TableElementType, { dx: gox + dx, dy: goy + dy })
                            : {
                                x: el.layout.x + gox + dx,
                                y: el.layout.y + goy + dy,
                                width: el.size.width,
                                height: el.size.height,
                              };
                    const currentRadius = radiusDragState?.id === el.id && radiusPreview != null
                      ? radiusPreview
                      : el.appearance.radius?.all ?? 0;

                    const startLineHandle = (handle: 'start' | 'end', e: React.MouseEvent) => {
                      if (el.interaction.locked) return;
                      e.stopPropagation(); e.preventDefault(); pushHistory();
                      const baseX = el.layout.x;
                      const baseY = el.layout.y;
                      const { endX: baseEndX, endY: baseEndY } = getLineEndOffset(lineEl!);
                      setLineHandleState({
                        id: el.id,
                        handle,
                        coordinateSpace: 'page',
                        startX: baseX,
                        startY: baseY,
                        startEndX: baseEndX,
                        startEndY: baseEndY,
                        startClientX: e.clientX,
                        startClientY: e.clientY,
                      });
                      setLineHandlePreview({ x: baseX, y: baseY, endX: baseEndX, endY: baseEndY });
                    };
                    const startResize = (handle: ResizeHandle, e: React.MouseEvent) => {
                      if (el.interaction.locked) return;
                      e.stopPropagation(); e.preventDefault(); pushHistory();
                      if (isGroup) {
                        // 编组缩放：快照子元素位置尺寸，随后按比例同步缩放
                        const groupEl = el as GroupElementType;
                        const base = groupResizeState?.id === el.id && groupResizePreview
                          ? groupResizePreview
                          : { x: el.layout.x, y: el.layout.y, width: el.size.width, height: el.size.height };
                        const childrenSnapshot = (groupEl.data.children ?? [])
                          .map((cid) => elements.find((e) => e.id === cid))
                          .filter((c): c is Element => !!c)
                          .map((c) => ({
                            id: c.id,
                            type: c.type,
                            x: c.layout.x,
                            y: c.layout.y,
                            width: c.size.width,
                            height: c.size.height,
                            ...(c.type === 'line' ? {
                              endX: (c as LineElementType).data.end?.x,
                              endY: (c as LineElementType).data.end?.y,
                            } : {}),
                          }));
                        setGroupResizeState({ id: el.id, handle, startX: base.x, startY: base.y, startW: base.width, startH: base.height, startClientX: e.clientX, startClientY: e.clientY, childrenSnapshot });
                        setGroupResizePreview({ x: base.x, y: base.y, width: base.width, height: base.height });
                        groupResizePreviewRef.current = { x: base.x, y: base.y, width: base.width, height: base.height };
                      } else {
                        const base = resizeState?.id === el.id && resizePreview ? resizePreview : { x: el.layout.x, y: el.layout.y, width: el.size.width, height: el.size.height };
                        setResizeState({
                          id: el.id,
                          handle,
                          startX: base.x,
                          startY: base.y,
                          startW: base.width,
                          startH: base.height,
                          startClientX: e.clientX,
                          startClientY: e.clientY,
                          ...(el.type === 'text'
                            ? {
                                startFontSize: getReferenceTextFontSize(el as TextElementType),
                                startTypography: { ...(el as TextElementType).typography },
                                startDocument: (el as TextElementType).data.document,
                              }
                            : {}),
                        });
                        setResizePreview({ x: base.x, y: base.y, width: base.width, height: base.height });
                        resizePreviewRef.current = { x: base.x, y: base.y, width: base.width, height: base.height };
                      }
                    };
                    const startRadiusDrag = (corner: 'tl' | 'tr' | 'br' | 'bl', e: React.MouseEvent) => {
                      if (el.interaction.locked) return;
                      e.stopPropagation(); e.preventDefault(); pushHistory();
                      const startRadius = Number(el.appearance.radius?.all) || 0;
                      setRadiusDragState({ id: el.id, corner, startRadius, startClientX: e.clientX, startClientY: e.clientY, startRectX: rect.x, startRectY: rect.y, startWidth: rect.width, startHeight: rect.height });
                      setRadiusPreview(startRadius);
                      radiusPreviewRef.current = startRadius;
                    };
                    const startRotate = (e: React.MouseEvent) => {
                      if (el.interaction.locked) return;
                      e.stopPropagation(); e.preventDefault(); pushHistory();
                      const pageEl = pageRef.current;
                      if (!pageEl) return;
                      const r = pageEl.getBoundingClientRect();
                      const cx = rect.x + rect.width / 2;
                      const cy = rect.y + rect.height / 2;
                      const centerClientX = r.left + cx * zoom;
                      const centerClientY = r.top + cy * zoom;
                      setRotateState({
                        id: el.id,
                        startRotation: el.layout.rotation,
                        startAngle: Math.atan2(e.clientY - centerClientY, e.clientX - centerClientX),
                        startClientX: e.clientX,
                        startClientY: e.clientY,
                        centerClientX,
                        centerClientY,
                      });
                      setRotatePreview(null);
                      rotatePreviewRef.current = null;
                    };
                    const effectiveRotation = rotateState?.id === el.id && rotatePreview != null ? rotatePreview : el.layout.rotation;
                    return (
                      <HandleLayer
                        element={el}
                        rect={rect}
                        zoom={zoom}
                        isLine={isLine}
                        lineEndX={lineEndX}
                        lineEndY={lineEndY}
                        currentRadius={currentRadius}
                        isRadiusDragging={radiusDragState?.id === el.id}
                        rotation={effectiveRotation}
                        resizeHandles={undefined}
                        onStartResize={startResize}
                        onStartLineHandle={startLineHandle}
                        onStartRotate={startRotate}
                        onStartRadiusDrag={startRadiusDrag}
                      />
                    );
                  })()}
    </div>
  );
}
